import {
  Dropdown,
  IDetailsRowProps,
  IRenderFunction,
  Modal,
  Stack,
  Text,
  useTheme,
} from '@fluentui/react';
import { CloseButton } from 'common/components/Buttons';
import {
  LISTVIEW_MODAL_HEIGHT,
  LISTVIEW_MODAL_WIDTH,
  TABLE_ROWS,
} from 'common/constants';
import { useCommonStyles } from 'common/styles';
import PURCHASE_ORDER_SEARCH_SUPPLIER from '../../../../list/PurchaseOrderSearch.graphql';
import PURCHASE_ORDER_FILTER_TOTAL from '../../../../list/PurchaseOrderSearchFilterTotals.graphql';

import {
  makeVar,
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery,
  useReactiveVar,
} from '@apollo/client';
import { FilterArrayType } from 'common/components/Filters';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData } from 'common/components/SearchBar';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import {
  PurchaseOrderSearchHints,
  PurchaseOrderSearchHintsVariables,
} from 'purchaseOrder/list/__generated__/PurchaseOrderSearchHints';
import { useColumns } from 'purchaseOrder/list/column.data';
import {
  buildPOSearchPayloads,
  CommonRowRenderer,
  createFooterAmountRenderer,
  dropdownStyles,
  PurchaseOrderItem,
  renderItemColumn,
  transformPOListData,
} from 'purchaseOrder/list/poListComponents';
import { POSearchBar } from 'purchaseOrder/list/POSearchBar';
import {
  getFilterParams,
  listTypeEnum,
  listTypeOptions,
  toOrderByVariable,
} from 'purchaseOrder/list/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { globalMode, setGlobalSearchText } from 'utility/cache/ui';
import {
  PurchaseOrderSearch,
  PurchaseOrderSearch_Query,
  PurchaseOrderSearchVariables,
} from '../../../../list/__generated__/PurchaseOrderSearch';

import {
  MultiSelectOption,
  MultiSelectTags,
} from 'common/components/MultiSelectTags';
import {
  PurchaseOrderApprovalCreate,
  PurchaseOrderApprovalCreateVariables,
} from 'purchaseOrder/__generated__/PurchaseOrderApprovalCreate';
import {
  PurchaseOrderSearchFilterTotals,
  PurchaseOrderSearchFilterTotalsVariables,
} from 'purchaseOrder/list/__generated__/PurchaseOrderSearchFilterTotals';
import { ExportPurchaseOrders } from 'purchaseOrder/list/ExportPurchaseOrders';
import { PurchaseOrderView } from 'purchaseOrder/view';
import PURCHASE_ORDER_SEARCH_HINTS from '../../../../list/PurchaseOrderSearchHints.graphql';
import PURCHASE_ORDER_APPROVAL_CREATE from '../../../../PurchaseOrderApprovalCreate.graphql';
import { useStyles } from './index.styles';

export const setPOListSupplierViewOptions = makeVar<{
  isSigning: boolean;
  isHistory: boolean;
  isSearchAll: boolean;
}>({ isSigning: true, isHistory: false, isSearchAll: true });
export interface SupplierModalPOListViewType {
  setPOListVisible: React.Dispatch<React.SetStateAction<boolean>>;
  supplierId: string;
}

export const SupplierModalPOListView: React.FC<SupplierModalPOListViewType> = ({
  setPOListVisible,
  supplierId,
}) => {
  const commonStyles = useCommonStyles();
  const theme = useTheme();

  const styles = useStyles();
  const POListSupplierViewOptions = useReactiveVar(
    setPOListSupplierViewOptions
  );

  const renderRef = useRef(false);
  const { columns } = useColumns();
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [hintsVisibility, setHintsVisibility] = useState<boolean>(false);
  const [searchHints, setSearchHints] = useState<number | null>();
  const [searchTextValue, setSearchTextValue] = useState<string | null>(null);
  const [gridColumns, setGridColumns] = useState<ColumnData[]>(columns);
  const globalSearchText = useReactiveVar(setGlobalSearchText);
  const globalState = useReactiveVar(globalMode);
  const [searchFilters, setSearchFilters] = useState<FilterArrayType[]>([]);
  const [POId, setPOId] = useState<string | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<PurchaseOrderItem[]>([]);
  const SearchFiltersUpdated = searchFilters.filter(
    (f) =>
      f.filterKey !== 'filterSupplierId' &&
      f.filterKey !== 'filterMultiStatusTypes'
  );
  const filterParams = getFilterParams(searchFilters);

  const filterMultiStatusTypes = searchFilters.find(
    (f) => f.filterKey === 'filterMultiStatusTypes'
  )?.value as number[];
  const {
    loading,
    data,
    networkStatus,
    refetch: refetchPOSupplier,
    fetchMore,
    variables: purchaseOrderSearchVariables,
  } = useQuery<PurchaseOrderSearch, PurchaseOrderSearchVariables>(
    PURCHASE_ORDER_SEARCH_SUPPLIER,
    {
      variables: {
        first: TABLE_ROWS,
        orderBy: ['PURCHASE_ORDER_NUMBER_DESC', 'PRIMARY_KEY_ASC'],
        isHistorySearch: false,
        isSigningSearch: true,
        isSearchAllData: true,
        filterSupplierId: supplierId,
        isLinkSearch: globalState,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
    }
  );

  const { data: purchaseOrderFilterTotal, refetch: refetchFilterTotal } =
    useQuery<
      PurchaseOrderSearchFilterTotals,
      PurchaseOrderSearchFilterTotalsVariables
    >(PURCHASE_ORDER_FILTER_TOTAL, {
      variables: {
        isHistorySearch: false,
        isSigningSearch: true,
        isSearchAllData: true,
        filterSupplierId: supplierId,
        isLinkSearch: globalState,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
    });
  const [fetchHints, { data: hintsData }] = useLazyQuery<
    PurchaseOrderSearchHints,
    PurchaseOrderSearchHintsVariables
  >(PURCHASE_ORDER_SEARCH_HINTS, {
    variables: {
      filterSupplierId: supplierId,
      searchRequest: {
        searchText: [searchTextValue ? searchTextValue : ''],
        searchHintId: searchHints ? searchHints : null,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const searchHintsOptions: MultiSelectOption[] =
    hintsData?.purchaseOrderSearchHints?.nodes.map(
      (hint) =>
        ({
          key: hint.id?.toString(),
          text: `${hint.caption} (${hint.resultsCount})`,
        } as MultiSelectOption)
    ) || [];
  const HINTS_AVAILABLE =
    searchHintsOptions.length > 0 && searchTextValue?.length! > 0;

  const refetching = loading && networkStatus !== NetworkStatus.fetchMore;
  const hasNextPage = data?.purchaseOrderSearch?.pageInfo.hasNextPage;

  const { totalAmount1, totalAmount2, totalAmount3, totalCount1 } = {
    ...purchaseOrderFilterTotal?.purchaseOrderSearchFilterTotals,
  };

  const transformedItems = useMemo(
    () =>
      refetching ? [] : transformPOListData(data?.purchaseOrderSearch?.nodes),
    [data?.purchaseOrderSearch?.nodes, refetching]
  );

  const _onColumnClick = useCallback(
    async (_ev?: React.MouseEvent<HTMLElement>, clickedColumn?: ColumnData) => {
      if (clickedColumn) {
        const { newColumns, desc } = getSortedColumns(
          clickedColumn,
          gridColumns
        );
        setGridColumns(newColumns);
        setSortOrderParam({
          column: clickedColumn.key,
          direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
        });
        await refetchPOSupplier({
          after: null,
          orderBy: toOrderByVariable({
            column: clickedColumn.key,
            direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
          }),
        });
      }
    },
    [gridColumns, refetchPOSupplier]
  );

  const handleSearchGlobal = (
    showMore: boolean,
    newHints: boolean,
    globalValue: boolean
  ) => {
    if (!showMore) setSelectedRows([]);

    const { variables, totalFilterVariables, variableHints, searchRequest } =
      buildPOSearchPayloads({
        showMore,
        newHints,
        globalValue,
        data,
        searchTextValue,
        searchHints,
        sortOrderParam,
        SearchFiltersUpdated,
        supplierId,
        filterMultiStatusTypes,
        POListViewOptions: POListSupplierViewOptions,
        filterParams,
      });

    if (showMore) {
      fetchMore?.({
        variables,
        updateQuery: (
          prevResult: PurchaseOrderSearch_Query,
          { fetchMoreResult }: { fetchMoreResult?: PurchaseOrderSearch_Query }
        ): PurchaseOrderSearch_Query => {
          if (!fetchMoreResult || !fetchMoreResult.purchaseOrderSearch) {
            return prevResult;
          }

          const prevSearch = prevResult?.purchaseOrderSearch;
          const newSearch = fetchMoreResult.purchaseOrderSearch;

          const isShowMore = Boolean(variables?.after);

          return {
            purchaseOrderSearch: {
              ...newSearch,
              nodes: isShowMore
                ? [...(prevSearch?.nodes ?? []), ...newSearch.nodes]
                : newSearch.nodes,
            },
          };
        },
      });
    } else {
      refetchPOSupplier(variables);
      refetchFilterTotal(totalFilterVariables);
    }

    if (newHints && searchRequest && !searchHints) {
      setSearchHints(null);
      fetchHints({ variables: variableHints });
    }
  };

  const _onRenderRow: IRenderFunction<IDetailsRowProps> = (
    props,
    defaultRender
  ) => (
    <CommonRowRenderer<PurchaseOrderItem>
      props={props}
      defaultRender={defaultRender}
      isUrgent={!Boolean((props?.item as PurchaseOrderItem)._urgencyLevel)}
    />
  );
  const handleSearch = (showMore: boolean, newHints: boolean) =>
    handleSearchGlobal(showMore, newHints, globalState);

  const handleSearchMemo = useCallback(handleSearch, [
    searchFilters,
    sortOrderParam,
    searchTextValue,
    searchHints,
    POListSupplierViewOptions,
  ]);

  useEffect(() => {
    if (renderRef.current) handleSearchMemo(false, true);
    else renderRef.current = true;
  }, [
    searchFilters,
    sortOrderParam,
    searchTextValue,
    searchHints,
    handleSearchMemo,
    POListSupplierViewOptions,
  ]);

  const [
    purchaseOrderApprovalCreate,
    { loading: purchaseOrderApprovalCreateLoading },
  ] = useMutation<
    PurchaseOrderApprovalCreate,
    PurchaseOrderApprovalCreateVariables
  >(PURCHASE_ORDER_APPROVAL_CREATE, { errorPolicy: 'all' });

  const showFooter =
    !refetching &&
    parseFloat(totalAmount1 || '0.0') > 0 &&
    (totalCount1 || 0) > 0;

  const totalsMap = {
    controlTotalAmount: totalAmount1,
    _baseCurrencyAmount: totalAmount2,
    _spotCurrencyAmount: totalAmount3,
  };

  const _renderDetailsFooterItemColumn = createFooterAmountRenderer({
    totalsMap,
    className: styles.footerTotal,
  });
  useEffect(() => {
    return () => {
      setPOListSupplierViewOptions({
        ...POListSupplierViewOptions,
        isSigning: true,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (POListSupplierViewOptions.isHistory) {
      const newColumns = gridColumns.map((item) => {
        if (item.key === 'transactionStamp')
          return { ...item, isVisible: false };
        else return item;
      });
      setGridColumns(newColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [POListSupplierViewOptions.isHistory]);

  if (POId) return <PurchaseOrderView POId={POId} setPOId={setPOId} />;

  return (
    <>
      <Modal isOpen>
        <Stack
          tokens={{ childrenGap: 20 }}
          className={commonStyles.listHeaderContainer}
          style={{
            height: LISTVIEW_MODAL_HEIGHT,
            width: LISTVIEW_MODAL_WIDTH + 70,
          }}
        >
          <Stack
            horizontal
            horizontalAlign="space-between"
            verticalAlign="center"
            className={commonStyles.listTitleContainer}
          >
            <Text variant="xLarge">Purchase Orders</Text>
            <Stack
              horizontal
              style={{ marginLeft: 'auto' }}
              tokens={{ childrenGap: 10 }}
            >
              <Dropdown
                placeholder="Select"
                selectedKey={
                  POListSupplierViewOptions.isSearchAll
                    ? listTypeEnum.all
                    : listTypeEnum.my
                }
                options={listTypeOptions}
                onChange={(event, option) => {
                  if (event) {
                    const isSearchAll = option?.key === listTypeEnum.all;
                    setPOListSupplierViewOptions({
                      ...POListSupplierViewOptions,
                      isSearchAll,
                    });
                  }
                }}
                styles={dropdownStyles(theme)}
              />

              <ExportPurchaseOrders
                selectedRows={selectedRows}
                isSigningSearch={POListSupplierViewOptions.isSigning}
                isHistorySearch={POListSupplierViewOptions.isHistory}
                isSearchAllData={POListSupplierViewOptions.isSearchAll}
                isLinkSearch={globalState}
                filterSupplierId={supplierId}
                searchFilters={searchFilters}
                purchaseOrderDocumentSearchRequest={
                  searchTextValue ? { searchText: [searchTextValue] } : null
                }
                isSelectedAll={
                  !!transformedItems?.length &&
                  selectedRows.length === transformedItems.length
                }
              />
            </Stack>
            <CloseButton
              onClick={() => {
                globalMode(false);
                setPOListVisible(false);
              }}
            />
          </Stack>

          <POSearchBar
            hintsAvailable={HINTS_AVAILABLE}
            isPOSupplierList
            columns={gridColumns}
            onToggleVisibility={setGridColumns}
            onRefresh={() => {
              handleSearch(false, false);
            }}
            onFilterChange={(filters) => {
              setSearchFilters(filters);
            }}
            onEnterPress={(value, globalSearch) => {
              setSearchHints(null);
              setHintsVisibility(false);
              setSearchTextValue(value);
              setGlobalSearchText(value);
            }}
            onHintsViewToggle={() =>
              setHintsVisibility((prevState) => !prevState)
            }
            onGlobalToggle={(value) => {
              setPOListSupplierViewOptions({
                ...POListSupplierViewOptions,
                isHistory: false,
                isSigning: true,
              });
              handleSearchGlobal(false, true, value);
            }}
            isSigning={POListSupplierViewOptions.isSigning}
          />
          {hintsVisibility && HINTS_AVAILABLE && (
            <MultiSelectTags
              multiSelect={false}
              options={searchHintsOptions}
              onSelectionChange={(selctedTags) => {
                const hints = selctedTags.map((item) => parseInt(item.key));
                setSearchHints(hints[0]);
              }}
            />
          )}

          <InfiniteList
            items={transformedItems || []}
            loading={loading}
            hasNextPage={hasNextPage}
            showFooter={showFooter}
            columns={gridColumns.filter((column) => column.isVisible)}
            onRenderItemColumn={(item, index, column) =>
              renderItemColumn({
                item,
                index,
                column,
                styles,
                globalSearchText,
                isModalView: true,
                setPOId,
              })
            }
            onRenderFooterItemColumn={_renderDetailsFooterItemColumn}
            onRenderRow={_onRenderRow}
            onColumnHeaderClick={_onColumnClick}
            onLoadMore={async () => await handleSearch(true, false)}
            onSelectionChanged={setSelectedRows}
            totalRowsCount={totalCount1}
          />
        </Stack>
      </Modal>
    </>
  );
};
